import classNames from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';
import { Box, Flex, Text } from '@atlaskit/primitives';
import { EntitlementDetails } from '@css-xp/graphtypes/src/generated-types-new';
import { CustomPanel, DynamicResultTable } from '@css-xp/base';
import { routePaths } from '@css-xp/ppc-constants';
import { getRouteUrl } from '@css-xp/ppc-utils';
import { emptyStateProp, NoData, renderTitleSubtitle } from '@css-xp/ppc-common';
import { N0 } from '@atlaskit/theme/colors';
import './subscriptions-by-bill-date.scss';

interface Props {
  billingDate: string;
  entitlements: EntitlementDetails[];
  focused?: boolean | null | undefined;
  txaId?: string;
  isDesignIteration1Enabled?: boolean;
}

const wrapInCell = (node?: React.ReactElement | string | null) => {
  return <div className="subscription-cell">{node}</div>;
};

const makeRows = (entitlements: EntitlementDetails[], txaId: string) => {
  const entitlementBaseRoute = getRouteUrl(routePaths.subscriptions, { txaId: txaId || '' });
  return entitlements.map((entitlement) => {
    const { entitlementNumber, productName, productId } = entitlement;
    const rowKey = `entitlement-${productId}`;
    return {
      key: rowKey,
      cells: [
        {
          content: wrapInCell(productName ?? 'Unnamed product'),
        },
        {
          content: wrapInCell(
            <Link
              to={`${entitlementBaseRoute}${entitlementNumber}`}
              data-testId="bill-date-entitlement-link"
              target="_blank"
              onClick={(e) => e.stopPropagation()}
            >
              {entitlementNumber || <NoData />}
            </Link>
          ),
        },
      ],
    };
  });
};

const getHeader = (billingDate: string) => {
  return (
    <div className="bill-date-header">
      <div className="bill-date-div">
        <span className="subheading">
          <p>Bill date</p>
        </span>
        <p className="bill-date">{billingDate}</p>
        <p className="view-subscription">View subscriptions under this bill date</p>
      </div>
    </div>
  );
};

const getHeaderV3 = (billingDate: string) => {
  return <div className="ml-8">{renderTitleSubtitle(billingDate, 'View subscriptions under this bill date')}</div>;
};

const SubscriptionsByBillDate = ({
  billingDate,
  entitlements,
  focused,
  txaId = '',
  isDesignIteration1Enabled,
}: Props) => {
  const header = isDesignIteration1Enabled ? getHeaderV3(billingDate) : getHeader(billingDate);

  const renderSubscriptionByBillDate = () => {
    if (isDesignIteration1Enabled) {
      return (
        <Box padding="space.250">
          <Flex gap="space.300" direction="column">
            {entitlements.map((entitlement) => {
              return (
                <Flex key={entitlement.id} direction="column">
                  <Text>{entitlement.productName}</Text>
                  <Text color="color.text.subtlest" size="small">
                    {entitlement.entitlementNumber}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        </Box>
      );
    }
    return (
      <div className="subscriptions-container">
        <DynamicResultTable
          head={{ cells: [] }}
          rows={makeRows(entitlements, txaId)}
          loadingSpinnerSize="small"
          emptyStateProps={emptyStateProp}
          boxShadow={false}
        />
      </div>
    );
  };

  return (
    <div className={classNames('subscriptions-by-bill-date-container', { focused })}>
      <CustomPanel
        expandedHeader={header}
        collapsedHeader={header}
        version={isDesignIteration1Enabled ? 3 : undefined}
        padding="15px 0 15px 40px"
        backgroundColor={N0}
      >
        {renderSubscriptionByBillDate()}
      </CustomPanel>
    </div>
  );
};

export default SubscriptionsByBillDate;
